import React, { useState, useEffect, useCallback } from 'react';
import api from '../utils/api';

const Settings = () => {
    const [settings, setSettings] = useState({});
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [tab, setTab] = useState('empresa');
    const [success, setSuccess] = useState('');
    const [error, setError] = useState('');
    const [logoPreview, setLogoPreview] = useState(null);

    const loadSettings = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await api.get('/settings');
            const flat = {};
            Object.entries(data).forEach(([k, v]) => { flat[k] = v.valor || ''; });
            setSettings(flat);
            if (flat.empresa_logo) setLogoPreview(flat.empresa_logo);
        } catch {
            setError('Error cargando configuración');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { loadSettings(); }, [loadSettings]);

    const showSuccess = (msg) => {
        setSuccess(msg); setError('');
        setTimeout(() => setSuccess(''), 4000);
    };

    const handleChange = (key, value) => {
        setSettings(prev => ({ ...prev, [key]: value }));
    };

    const saveSection = async (keys) => {
        setSaving(true);
        setError('');
        try {
            const partial = {};
            keys.forEach(k => { partial[k] = settings[k] || ''; });
            await api.put('/settings/bulk', { settings: partial });
            showSuccess('Configuración guardada correctamente');

            // Actualizar CSS si son colores
            if (keys.includes('color_primario')) {
                document.documentElement.style.setProperty('--color-primary', settings.color_primario);
            }
            if (keys.includes('color_secundario')) {
                document.documentElement.style.setProperty('--color-secondary', settings.color_secundario);
            }
            if (keys.includes('color_fondo')) {
                document.documentElement.style.setProperty('--color-fondo', settings.color_fondo);
            }
        } catch (err) {
            setError(err.response?.data?.error || 'Error guardando configuración');
        } finally {
            setSaving(false);
        }
    };

    const tabs = [
        { id: 'empresa', label: '🏢 Empresa' },
        { id: 'apariencia', label: '🎨 Apariencia' },
        { id: 'correo', label: '📧 Correo' },
        { id: 'seguridad', label: '🔒 Seguridad' },
    ];

    if (loading) {
        return <div className="text-center text-gray-400 py-12">Cargando configuración...</div>;
    }

    return (
        <div className="max-w-3xl mx-auto">
            <h2 className="text-2xl font-bold text-gray-800 mb-6">Configuración</h2>

            {success && <div className="bg-green-50 border border-green-200 text-green-700 text-sm rounded-lg px-4 py-3 mb-4">{success}</div>}
            {error && <div className="bg-red-50 border border-red-200 text-red-600 text-sm rounded-lg px-4 py-3 mb-4">{error}</div>}

            <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
                {/* Tabs */}
                <div className="flex border-b border-gray-100">
                    {tabs.map(t => (
                        <button
                            key={t.id}
                            onClick={() => { setTab(t.id); setSuccess(''); setError(''); }}
                            className={`flex-1 py-3 text-sm font-medium transition-colors ${
                                tab === t.id ? 'border-b-2' : 'text-gray-500 hover:text-gray-700'
                            }`}
                            style={tab === t.id ? { borderColor: 'var(--color-primary)', color: 'var(--color-primary)' } : {}}
                        >
                            {t.label}
                        </button>
                    ))}
                </div>

                <div className="p-6">

                    {/* Tab: Empresa */}
                    {tab === 'empresa' && (
                        <div className="space-y-5">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Nombre de la empresa</label>
                                <input
                                    value={settings.empresa_nombre || ''}
                                    onChange={e => handleChange('empresa_nombre', e.target.value)}
                                    className="w-full border border-gray-300 rounded-lg px-4 py-2.5 text-sm focus:outline-none"
                                    placeholder="Time Telco"
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">Logo</label>
                                <div className="flex items-center gap-4">
                                    <div className="w-20 h-20 border-2 border-dashed border-gray-300 rounded-xl flex items-center justify-center overflow-hidden">
                                        {logoPreview ? (
                                            <img src={logoPreview} alt="Logo" className="w-full h-full object-contain" />
                                        ) : (
                                            <span className="text-gray-400 text-xs text-center px-2">Sin logo</span>
                                        )}
                                    </div>
                                    <div className="flex-1">
                                        <input
                                            value={settings.empresa_logo || ''}
                                            onChange={e => { handleChange('empresa_logo', e.target.value); setLogoPreview(e.target.value); }}
                                            className="w-full border border-gray-300 rounded-lg px-4 py-2.5 text-sm focus:outline-none"
                                            placeholder="https://... URL del logo"
                                        />
                                        <p className="text-xs text-gray-400 mt-1">Ingresa la URL de tu logo (JPG, PNG, SVG)</p>
                                    </div>
                                </div>
                            </div>

                            <button
                                onClick={() => saveSection(['empresa_nombre', 'empresa_logo'])}
                                disabled={saving}
                                className="w-full text-white font-semibold py-2.5 rounded-lg disabled:opacity-60"
                                style={{ backgroundColor: 'var(--color-primary)' }}
                            >
                                {saving ? 'Guardando...' : 'Guardar'}
                            </button>
                        </div>
                    )}

                    {/* Tab: Apariencia */}
                    {tab === 'apariencia' && (
                        <div className="space-y-5">
                            <p className="text-sm text-gray-500">Los cambios se aplican en tiempo real al guardar.</p>

                            <div className="grid grid-cols-3 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">Color primario</label>
                                    <div className="flex items-center gap-2">
                                        <input
                                            type="color"
                                            value={settings.color_primario || '#0057B7'}
                                            onChange={e => handleChange('color_primario', e.target.value)}
                                            className="w-12 h-10 rounded cursor-pointer border border-gray-300"
                                        />
                                        <input
                                            value={settings.color_primario || ''}
                                            onChange={e => handleChange('color_primario', e.target.value)}
                                            className="flex-1 border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none"
                                            placeholder="#0057B7"
                                        />
                                    </div>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">Color secundario</label>
                                    <div className="flex items-center gap-2">
                                        <input
                                            type="color"
                                            value={settings.color_secundario || '#00A651'}
                                            onChange={e => handleChange('color_secundario', e.target.value)}
                                            className="w-12 h-10 rounded cursor-pointer border border-gray-300"
                                        />
                                        <input
                                            value={settings.color_secundario || ''}
                                            onChange={e => handleChange('color_secundario', e.target.value)}
                                            className="flex-1 border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none"
                                            placeholder="#00A651"
                                        />
                                    </div>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">Color de fondo</label>
                                    <div className="flex items-center gap-2">
                                        <input
                                            type="color"
                                            value={settings.color_fondo || '#F5F5F5'}
                                            onChange={e => handleChange('color_fondo', e.target.value)}
                                            className="w-12 h-10 rounded cursor-pointer border border-gray-300"
                                        />
                                        <input
                                            value={settings.color_fondo || ''}
                                            onChange={e => handleChange('color_fondo', e.target.value)}
                                            className="flex-1 border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none"
                                            placeholder="#F5F5F5"
                                        />
                                    </div>
                                </div>
                            </div>

                            {/* Preview */}
                            <div className="border border-gray-200 rounded-xl p-4">
                                <p className="text-xs text-gray-400 mb-3">Vista previa</p>
                                <div className="flex gap-3">
                                    <div className="w-24 h-10 rounded-lg flex items-center justify-center text-white text-xs font-semibold"
                                        style={{ backgroundColor: settings.color_primario || '#0057B7' }}>
                                        Primario
                                    </div>
                                    <div className="w-24 h-10 rounded-lg flex items-center justify-center text-white text-xs font-semibold"
                                        style={{ backgroundColor: settings.color_secundario || '#00A651' }}>
                                        Secundario
                                    </div>
                                    <div className="w-24 h-10 rounded-lg flex items-center justify-center text-gray-600 text-xs font-semibold border border-gray-200"
                                        style={{ backgroundColor: settings.color_fondo || '#F5F5F5' }}>
                                        Fondo
                                    </div>
                                </div>
                            </div>

                            <button
                                onClick={() => saveSection(['color_primario', 'color_secundario', 'color_fondo'])}
                                disabled={saving}
                                className="w-full text-white font-semibold py-2.5 rounded-lg disabled:opacity-60"
                                style={{ backgroundColor: 'var(--color-primary)' }}
                            >
                                {saving ? 'Guardando...' : 'Guardar colores'}
                            </button>
                        </div>
                    )}

                    {/* Tab: Correo */}
                    {tab === 'correo' && (
                        <div className="space-y-4">
                            <p className="text-sm text-gray-500">Configuración SMTP para envío de correos del sistema.</p>
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Servidor SMTP</label>
                                    <input
                                        value={settings.smtp_host || ''}
                                        onChange={e => handleChange('smtp_host', e.target.value)}
                                        className="w-full border border-gray-300 rounded-lg px-4 py-2.5 text-sm focus:outline-none"
                                        placeholder="mail.time.net.co"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Puerto</label>
                                    <input
                                        value={settings.smtp_port || ''}
                                        onChange={e => handleChange('smtp_port', e.target.value)}
                                        className="w-full border border-gray-300 rounded-lg px-4 py-2.5 text-sm focus:outline-none"
                                        placeholder="587"
                                    />
                                </div>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Usuario SMTP</label>
                                <input
                                    value={settings.smtp_user || ''}
                                    onChange={e => handleChange('smtp_user', e.target.value)}
                                    className="w-full border border-gray-300 rounded-lg px-4 py-2.5 text-sm focus:outline-none"
                                    placeholder="no-reply@time.net.co"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Contraseña SMTP</label>
                                <input
                                    type="password"
                                    value={settings.smtp_pass || ''}
                                    onChange={e => handleChange('smtp_pass', e.target.value)}
                                    className="w-full border border-gray-300 rounded-lg px-4 py-2.5 text-sm focus:outline-none"
                                    placeholder="••••••••"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Correo remitente</label>
                                <input
                                    value={settings.smtp_from || ''}
                                    onChange={e => handleChange('smtp_from', e.target.value)}
                                    className="w-full border border-gray-300 rounded-lg px-4 py-2.5 text-sm focus:outline-none"
                                    placeholder="no-reply@time.net.co"
                                />
                            </div>
                            <button
                                onClick={() => saveSection(['smtp_host', 'smtp_port', 'smtp_user', 'smtp_pass', 'smtp_from'])}
                                disabled={saving}
                                className="w-full text-white font-semibold py-2.5 rounded-lg disabled:opacity-60"
                                style={{ backgroundColor: 'var(--color-primary)' }}
                            >
                                {saving ? 'Guardando...' : 'Guardar configuración de correo'}
                            </button>
                        </div>
                    )}

                    {/* Tab: Seguridad */}
                    {tab === 'seguridad' && (
                        <div className="space-y-4">
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Longitud mínima de contraseña</label>
                                    <input
                                        type="number"
                                        min="6" max="32"
                                        value={settings.password_min_length || '8'}
                                        onChange={e => handleChange('password_min_length', e.target.value)}
                                        className="w-full border border-gray-300 rounded-lg px-4 py-2.5 text-sm focus:outline-none"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Días antes de expirar contraseña</label>
                                    <input
                                        type="number"
                                        min="0"
                                        value={settings.password_expira_dias || '90'}
                                        onChange={e => handleChange('password_expira_dias', e.target.value)}
                                        className="w-full border border-gray-300 rounded-lg px-4 py-2.5 text-sm focus:outline-none"
                                    />
                                    <p className="text-xs text-gray-400 mt-1">0 = nunca expira</p>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Horas máximas de sesión</label>
                                    <input
                                        type="number"
                                        min="1"
                                        value={settings.sesion_max_horas || '24'}
                                        onChange={e => handleChange('sesion_max_horas', e.target.value)}
                                        className="w-full border border-gray-300 rounded-lg px-4 py-2.5 text-sm focus:outline-none"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Días de acceso para invitados</label>
                                    <input
                                        type="number"
                                        min="1"
                                        value={settings.invitado_dias || '10'}
                                        onChange={e => handleChange('invitado_dias', e.target.value)}
                                        className="w-full border border-gray-300 rounded-lg px-4 py-2.5 text-sm focus:outline-none"
                                    />
                                </div>
                            </div>
                            <button
                                onClick={() => saveSection(['password_min_length', 'password_expira_dias', 'sesion_max_horas', 'invitado_dias'])}
                                disabled={saving}
                                className="w-full text-white font-semibold py-2.5 rounded-lg disabled:opacity-60"
                                style={{ backgroundColor: 'var(--color-primary)' }}
                            >
                                {saving ? 'Guardando...' : 'Guardar configuración de seguridad'}
                            </button>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default Settings;
